import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { formatToMaxFourDigits } from '@flexo/general';

const ENV = (import.meta as any).env;
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const isWebSafari = ENV?.VITE_APP_IS_WEB === 'true' && isSafari;

export default function HorizontalChartUnified(props: any) {
  const ref: any = useRef(null);
  const { t } = useTranslation();
  const ThemeStore = useSelector((state: any) => state.theme);
  const [data, setData] = useState<any>(null);
  const [customData, setCustomData] = useState<any>(null);
  const [hoveredBarKey, setHoveredBarKey] = useState<any>(null);

  const isDouble = Array.isArray(props?.data?.graphKeys?.[0]);
  const maxValue = props?.data?.maxValue || 0;
  const activeTooltip = props?.data?.activeTooltip;
  const setActiveTooltip = props?.data?.setActiveTooltip;

  const CustomTooltip: any = ({ active, payload }) => {
    if (isDouble && (activeTooltip !== hoveredBarKey + props?.data?.key)) return null;
    if (active && payload && payload.length) {
      const activePayload = payload.find(item => item.dataKey === hoveredBarKey);
      const unit = props?.data?.kpi?.find((datum: any) => datum.name === hoveredBarKey)?.unit || '';
      if (isDouble && props?.data?.combinedKeys) {
        return [props?.data?.combinedKeys[0].includes(activePayload?.dataKey), props?.data?.combinedKeys[1].includes(activePayload?.dataKey)]
          .map((x, i) => {
            if (x) {
              const firstActivePayload = payload.find(item => item.dataKey === props?.data?.combinedKeys[i][0]);
              const secondActivePayload = payload.find(item => item.dataKey === props?.data?.combinedKeys[i][1]);
              if (firstActivePayload === undefined || secondActivePayload === undefined) { return <></>; }
              return (
                <div
                  style={{
                    backgroundColor: 'var( --hive-greyscale-100 )',
                    padding: '10px',
                    borderRadius: '8px',
                    fontFamily: 'Flexo',
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'row',
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      borderBottom: `solid 4px ${firstActivePayload.fill}`,
                    }}
                  >
                    <strong className={`${isWebSafari ? 'font-flexo-light' : 'font-flexo-semibold'}`}>
                      {t(`widgets.horizontalChart.${props?.data?.context}.${props?.data?.combinedKeys[i][0]}`)}
                    </strong>{' '}
                    {props?.data?.context === 'economics'
                      ? `${unit} ${formatToMaxFourDigits(data[0][firstActivePayload.id])} `
                      : `${formatToMaxFourDigits(data[0][firstActivePayload.id])} ${unit}`}
                    {' | '}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      borderBottom: `solid 4px ${secondActivePayload.fill}`,
                    }}
                  >
                    <strong className={`${isWebSafari ? 'font-flexo-light' : 'font-flexo-semibold'}`} >
                      &nbsp;
                      {t(`widgets.horizontalChart.${props?.data?.context}.${props?.data?.combinedKeys[i][1]}`)}
                    </strong>{' '}
                    {props?.data?.context === 'economics'
                      ? `${unit} ${formatToMaxFourDigits(data[0][secondActivePayload.id])} `
                      : `${formatToMaxFourDigits(data[0][secondActivePayload.id])} ${unit}`}
                  </p>
                </div>
              );
            }
          });
      }
      if (activePayload) {
        return (
          <div
            style={{
              backgroundColor: 'var( --hive-greyscale-100 )',
              padding: '10px',
              borderRadius: '8px',
              fontFamily: 'Flexo',
              fontSize: 14,
            }}
          >
            <p
              style={{
                margin: 0,
                borderBottom: `solid 4px ${activePayload.fill}`,
              }}
            >
              <strong className={`${isWebSafari ? 'font-flexo-light' : 'font-flexo-semibold'}`}>
                {t(`widgets.horizontalChart.${props?.data?.context}.${activePayload.dataKey}`)}
              </strong>{' '}
              {props?.data?.context === 'economics'
                ? `${unit} ${formatToMaxFourDigits(activePayload.value)} `
                : `${formatToMaxFourDigits(activePayload.value)} ${unit}`}
            </p>
          </div>
        );
      }
    }
    return null;
  };

  const CustomBarShape = (_props: any) => {
    const { x, y, width, height, fill, keyName, index, array } = _props;
    let _x = x;
    if (isDouble && props?.data?.combinedKeys) {
      let firstvalue = 0;
      let secondValue = 0;
      let shouldHideTheNextBar = false;
      if (props?.data?.combinedKeys[0][1] === keyName) {
        firstvalue = _props[props?.data?.combinedKeys[0][0]];
        secondValue = _props[props?.data?.combinedKeys[0][1]];
      } else if (props?.data?.combinedKeys[1][1] === keyName) {
        firstvalue = _props[props?.data?.combinedKeys[1][0]];
        secondValue = _props[props?.data?.combinedKeys[1][1]];
      }
      if (props?.data?.combinedKeys[0][0] === keyName) {
        if (_props[props?.data?.combinedKeys[0][0]] === _props[props?.data?.combinedKeys[0][1]]) {
          shouldHideTheNextBar = true;
        }
      }
      if (props?.data?.combinedKeys[1][0] === keyName) {
        if (_props[props?.data?.combinedKeys[1][0]] === _props[props?.data?.combinedKeys[1][1]]) {
          shouldHideTheNextBar = true;
        }
      }
      if (firstvalue !== 0) {
        _x = x - 3;
      }
      const sortedActiveKeys = [...props?.data?.activeKeys || []].sort((a: any, b: any) => {
        return props?.data?.graphKeys.indexOf(a) - props?.data?.graphKeys.indexOf(b);
      });
      const firstCommonKey = sortedActiveKeys.find((key: any) => props?.data?.graphKeys.includes(key));
      let isFirstCommonKey = firstCommonKey === keyName;
      const keyIndex = (props?.data?.graphKeys || []).indexOf(keyName);
      const combinedKeysIndex = props?.data?.combinedKeys[props?.data?.combinedKeysIndex].findIndex((key: any) => key === keyName);
      function areItemsBeforeNull(index: number) {
        return props?.data?.graphKeys.slice(0, index).every((item: any) => ((data[0][item] === null || data[0][item] === undefined)));
      }
      const indexBeforeAreNull = areItemsBeforeNull(keyIndex);
      const lastCommonKey = [...sortedActiveKeys].reverse().find((key: any) => (props?.data?.graphKeys.includes(key)));
      let isLastCommonKey = lastCommonKey === keyName || shouldHideTheNextBar;
      if (combinedKeysIndex === 0 && customData && customData[0][props?.data?.combinedKeys[props?.data?.combinedKeysIndex][1]] < 0.05) {
        isLastCommonKey = true;
      }
      return (
        <g>
          <rect
            x={(index === array.length - 1 || isLastCommonKey) ? _x - 6 : _x}
            y={y}
            width={width}
            height={height}
            fill={fill}
            style={
              isFirstCommonKey && isLastCommonKey
                ? { clipPath: 'inset(0% 4px 0% 4px round 10px)' }
                : isFirstCommonKey
                  ? { clipPath: 'inset(0px 4px 0px 0px)' }
                  : isLastCommonKey
                    ? { clipPath: 'inset(0px 0px 0px 4px)' }
                    : indexBeforeAreNull
                      ? { clipPath: 'inset(0px 4px 0px 0px)' }
                      : {}
            }
            rx={isFirstCommonKey || isLastCommonKey || indexBeforeAreNull ? 4 : 0}
          />
        </g>
      );
    } else {
      if (!props?.data?.combinedKeys) {
        return <g></g>;
      }
      const isActive = props.data.activeKeys.includes(keyName);
      const sortedActiveKeys = [...props?.data?.activeKeys || []].sort((a: any, b: any) => {
        return props?.data?.graphKeys.indexOf(a) - props?.data?.graphKeys.indexOf(b);
      });
      const firstCommonKey = sortedActiveKeys.find((key: any) => props?.data?.graphKeys.includes(key));
      const isFirstCommonKey = firstCommonKey === keyName;
      const keyIndex = props?.data?.graphKeys.indexOf(keyName);
      function areItemsBeforeNull(index: number) {
        return props?.data?.graphKeys.slice(0, index).every((item: any) => ((data[0][item] === null || data[0][item] === undefined)));
      }
      const indexBeforeAreNull = areItemsBeforeNull(keyIndex);
      const lastCommonKey = [...sortedActiveKeys].reverse().find((key: any) => props?.data?.graphKeys.includes(key));
      const isSecondLastCommonKey = [...sortedActiveKeys].reverse()[1] === keyName;
      let isLastCommonKey = lastCommonKey === keyName;
      const lastKeyValue = props?.data?.kpi?.find((datum: any) => datum?.name === lastCommonKey)?.value || 0;
      if (isSecondLastCommonKey && lastKeyValue === 0) {
        isLastCommonKey = true;
      }
      return (
        <g>
          <rect
            x={index === array.length - 1 ? x - 6 : x}
            y={y}
            width={width}
            height={height}
            fill={isActive ? fill : 'transparent'}
            style={
              isFirstCommonKey && isLastCommonKey
                ? { clipPath: 'inset(0% 3px 0% 3px round 10px)' }
                : isFirstCommonKey
                  ? { clipPath: 'inset(0px 3px 0px 0px)' }
                  : isLastCommonKey
                    ? { clipPath: 'inset(0px 0px 0px 3px)' }
                    : indexBeforeAreNull
                      ? { clipPath: 'inset(0px 3px 0px 0px)' }
                      : {}
            }
            rx={isFirstCommonKey || isLastCommonKey || indexBeforeAreNull ? 4 : 0}
          />
          {(!isLastCommonKey && !props?.data?.combinedKeys?.includes(keyName)) && (
            <rect
              x={x + width - 3}
              y={y}
              width={3}
              height={height}
              fill="#FFFFFF"
            />
          )}
        </g>
      );
    }
  };

  useEffect(() => {
    if (props?.data?.kpi && props?.data?.graphKeys) {
      if (!isDouble) {
        const _data = props.data.kpi
          .filter((datum: any) => (props.data.activeKeys.includes(datum.name) && datum?.value !== null))
          .reduce(
            (prev: any, next: any) => ({
              ...prev,
              [next.name]: next.value,
            }),
            { name: t(props.data.rankingKpiName) }
          );
        setData([_data]);
      } else {
        let _data = props.data.kpi
          .filter((datum: any) => (props.data.activeKeys.includes(datum.name) && datum?.value !== null))
          .reduce(
            (prev: any, next: any) => ({
              ...prev,
              [next.name]: next.value,
            }),
            { name: t(props.data.rankingKpiName) }
          );
        setCustomData([{
          ..._data,
          [props?.data?.combinedKeys[props?.data?.combinedKeysIndex][1]]: _data[props?.data?.combinedKeys[props?.data?.combinedKeysIndex][1]] - _data[props?.data?.combinedKeys[props?.data?.combinedKeysIndex][0]]
        }]);
        setData([_data]);
      }
    }
  }, [props.data.kpi, props.data.activeKeys]);

  try {
    return data ? (
      <ResponsiveContainer width={'90%'} height={20} ref={ref}>
        <BarChart
          layout="vertical"
          data={data}
          margin={{ top: 4, right: 0, left: -50, bottom: 4 }}
        >
          <XAxis type="number" hide axisLine={false} tick={false} label={''} domain={[0, maxValue || 'auto']} />
          <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={false} label={''} />
          <Tooltip isAnimationActive={false} content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
          {(isDouble ? props?.data?.graphKeys.flat() : props?.data?.graphKeys).map((key: string, index: number, array: any) => (
            <Bar
              isAnimationActive
              key={key}
              id={key}
              dataKey={key}
              stackId={props.data.rankingKpiName}
              onMouseEnter={() => setHoveredBarKey(key)}
              onMouseLeave={() => setHoveredBarKey(null)}
              fill={`${ThemeStore?.colors[props?.data?.colors[index].color]}${props.data?.colors[index].colorShade}`}
              shape={(barProps: any) => (
                <CustomBarShape
                  {...barProps}
                  keyName={barProps.dataKey}
                  index={index}
                  array={array}
                />
              )}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    ) : null;
  } catch (error) {
    console.log('error', error);
    return null;
  }
} 